import { Controller } from "@hotwired/stimulus";
import { contributionForGap, applyAddOnToScenarioTotals } from "../scenario_modeling/contribution";

// Keeps the two Add-On Contribution for Workers without a Living Wage Gap fields in sync:
//   amount  = (percent / 100) * livingWageContribution
//   percent = (amount / livingWageContribution) * 100
//
// Living wage contribution is the gap-only amount (no taxes, other costs, or add-on).
// It is read live from the All Intermediaries footer gap, or the modeling summary
// gross contribution, so gap-target edits stay in sync.
export default class extends Controller {
  static targets = ["percentInput", "amountInput", "actionRequired", "linkHint"];

  static values = {
    livingWageContribution: { type: Number, default: 0 },
  };

  connect() {
    this._onContributionsUpdated = this._handleContributionsUpdated.bind(this);
    this._onGapTargetChange = this._handleGapTargetChange.bind(this);
    this._onPayloadApplied = () => queueMicrotask(() => this._handleContributionsUpdated());
    window.addEventListener("scenario-modeling:contributions-updated", this._onContributionsUpdated);
    window.addEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.addEventListener("scenario-modeling:payload-applied", this._onPayloadApplied);
    this._syncFromExistingInputs();
    this._syncLinkHint();
    this._dispatchTopUpChange();
  }

  disconnect() {
    window.removeEventListener("scenario-modeling:contributions-updated", this._onContributionsUpdated);
    window.removeEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.removeEventListener("scenario-modeling:payload-applied", this._onPayloadApplied);
  }

  onPercentInput() {
    if (this._canDerivePercentToAmount()) {
      const percent = this._numeric(this.percentInputTarget.value);
      const amount = (percent / 100) * this._liveLivingWageContribution();
      this.amountInputTarget.value = this._isBlank(this.percentInputTarget.value)
        ? ""
        : String(Math.round(amount));
    }
    this._syncActionRequired();
    this._syncLinkHint();
    this._dispatchTopUpChange();
  }

  onAmountInput() {
    if (this._canDeriveAmountToPercent()) {
      const amount = this._numeric(this.amountInputTarget.value);
      const percent = (amount / this._liveLivingWageContribution()) * 100;
      this.percentInputTarget.value = this._isBlank(this.amountInputTarget.value)
        ? ""
        : String(this._round(percent, 2));
    }
    this._syncActionRequired();
    this._syncLinkHint();
    this._dispatchTopUpChange();
  }

  _handleGapTargetChange() {
    queueMicrotask(() => this._handleContributionsUpdated());
  }

  _handleContributionsUpdated(event) {
    const fromEvent = Number(event?.detail?.livingWageContribution);
    if (Number.isFinite(fromEvent) && fromEvent >= 0) {
      this.livingWageContributionValue = fromEvent;
    }

    this._syncLinkHint();

    if (!this._isBlank(this.percentInputTarget?.value) && this._canDerivePercentToAmount()) {
      this.onPercentInput();
    } else if (!this._isBlank(this.amountInputTarget?.value) && this._canDeriveAmountToPercent()) {
      this.onAmountInput();
    }
  }

  _syncFromExistingInputs() {
    if (!this.hasPercentInputTarget || !this.hasAmountInputTarget) return;

    if (!this._isBlank(this.percentInputTarget.value) && this._isBlank(this.amountInputTarget.value)) {
      this.onPercentInput();
    } else if (!this._isBlank(this.amountInputTarget.value) && this._isBlank(this.percentInputTarget.value)) {
      this.onAmountInput();
    }
  }

  _hasInputTargets() {
    return this.hasPercentInputTarget && this.hasAmountInputTarget;
  }

  _canDerivePercentToAmount() {
    return this._hasInputTargets() && this._liveLivingWageContribution() > 0;
  }

  _canDeriveAmountToPercent() {
    return this._hasInputTargets() && this._liveLivingWageContribution() > 0;
  }

  _canLink() {
    return this._canDerivePercentToAmount() || this._canDeriveAmountToPercent();
  }

  // A DOM-derived value is authoritative even when it is 0 (e.g. gap target set
  // to 0, or every facility deselected). Only fall back to the cached event
  // value when no DOM source is available at all.
  _liveLivingWageContribution() {
    const fromDom = this._readLivingWageContributionFromDom();
    if (fromDom != null) return fromDom;

    const fromEventValue = this.livingWageContributionValue;
    return fromEventValue > 0 ? fromEventValue : 0;
  }

  // Returns null when neither DOM source holds a usable number (missing nodes,
  // or placeholder text such as "…" while a refresh is in flight).
  _readLivingWageContributionFromDom() {
    const footer = document.querySelector(
      '[data-scenario-modeling-intermediaries-target="totalContributionValue"]',
    );
    if (footer) {
      const gap = Number(footer.dataset.gapPayment);
      if (Number.isFinite(gap)) {
        return contributionForGap(gap, this._gapTarget(), 0, 0);
      }
    }

    const gross = document.querySelector(
      '[data-scenario-modeling-summary-target="grossContribution"]',
    );
    if (gross) {
      const normalized = String(gross.textContent ?? "").replace(/,/g, "").trim();
      const parsed = Number.parseFloat(normalized);
      if (Number.isFinite(parsed)) return parsed;
    }

    return null;
  }

  _gapTarget() {
    const input = document.getElementById("scenario_lw_gap_closure_target");
    const parsed = Number.parseFloat(input?.value);
    return Number.isFinite(parsed) ? parsed : 100;
  }

  _syncLinkHint() {
    if (!this.hasLinkHintTarget) return;

    this.linkHintTarget.classList.toggle("hidden", this._canLink());
  }

  _syncActionRequired() {
    if (!this.hasActionRequiredTarget) return;

    const missing =
      this._isBlank(this.percentInputTarget?.value) && this._isBlank(this.amountInputTarget?.value);
    this.actionRequiredTarget.classList.toggle("hidden", !missing);
  }

  _parseLocaleNumber(value) {
    const normalized = String(value ?? "").replace(/,/g, "").trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  _numeric(value) {
    return this._parseLocaleNumber(value);
  }

  _isBlank(value) {
    return String(value ?? "").trim() === "";
  }

  _round(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  _dispatchTopUpChange() {
    applyAddOnToScenarioTotals();
    window.dispatchEvent(new CustomEvent("scenario-modeling:top-up-change"));
  }
}
